import { PageController } from '@client/components/_core/layout/PageController';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { SupplierEdit } from '@client/components/suppliers/SupplierEdit';
import { useIsStillOnRoute } from '@client/hooks/navigation/useIsStillOnRoute';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { trpcOptions } from '@client/trpc/client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { claimFormStore } from '../store';

/**
 * CUR-2672 (PAY-F3): New supplier from the Add payment drawer's payee list: today's supplier form.
 * The payee list offers only suppliers linked to the claim through a contact, so the new supplier is
 * linked to this claim with its main contact, as the claim's Supplier contacts drawer links one.
 */
export const Route = createFileRoute('/console/$orgId/claims/details/$id/financials/payments/add/suppliers/add')({
    staticData: {
        presentation: 'overlay',
        metadata: {
            title: 'New supplier',
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    const { id: claimId } = Route.useParams();
    const navigate = useNavigate();
    const isStillOnRoute = useIsStillOnRoute();
    const queryClient = useQueryClient();
    const reload = claimFormStore.use.reload();
    // A closed claim's Financials offers no Add payment, so only a direct link lands here: the supplier
    // would be made and then refused a link to the claim.
    const isClaimReadonly = claimFormStore.use.isReadonly();
    useEffect(() => {
        if (isClaimReadonly) navigate({ moveInMatches: -1 });
    }, [isClaimReadonly, navigate]);
    // From the create until this leaves: the form's values would otherwise make a second supplier.
    const [isLinking, setIsLinking] = useState(false);

    // This editor, not just its route: a New supplier reopened at the same address is another editor,
    // which an earlier one's late link must not close.
    const isMounted = useRef(false);
    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    // The supplier exists once `SupplierEdit` reports it, so this returns to the drawer whatever the
    // link's outcome: staying would leave a form whose Save creates a second supplier. A user who has
    // already left is not brought back, but the link still completes.
    const returnToDrawer = () => {
        if (isMounted.current && isStillOnRoute()) navigate({ moveInMatches: -1 });
    };

    // Callbacks on the hook, not on `mutate`, so the payee list refreshes even after the user has left.
    const { mutate: linkToClaim } = useMutation(
        trpcOptions.claim.reassignClaimSupplierContacts.mutationOptions({
            onSuccess() {
                void queryClient.invalidateQueries(trpcOptions.supplier.listSuppliersForSelect.pathFilter());
                void queryClient.invalidateQueries(trpcOptions.contact.listContactsForSelect.pathFilter());
                // The claim's Contacts tab lists its suppliers from the claim record.
                void reload();
            },
            onError(error) {
                errorMessage.show(error);
            },
            onSettled: returnToDrawer,
        }),
    );

    const linkNewSupplier = async ({ supplierId }: { supplierId: bigint }) => {
        setIsLinking(true);
        let contactIds: bigint[];
        try {
            // A new supplier has at most its main contact.
            const { rows } = await queryClient.fetchQuery(
                trpcOptions.contact.listContactsForSelect.queryOptions({
                    where: { type: 'SUPPLIER', entity: 'supplier', entityId: supplierId },
                }),
            );
            contactIds = rows.map(({ id }) => id);
        } catch (error) {
            errorMessage.show(error);
            returnToDrawer();
            return;
        }

        // A supplier joins the claim through a contact; the payee list has nothing to offer without one.
        if (contactIds.length === 0) {
            message.warning('The new supplier has no contact, so it was not added to this claim as a payee.');
            returnToDrawer();
            return;
        }

        linkToClaim({ claimId, supplierId, contactIds });
    };

    if (isClaimReadonly) return null;

    return (
        <PageController route={Route}>
            <SupplierEdit isBusy={isLinking} onSuccess={(supplier) => void linkNewSupplier(supplier)} />
        </PageController>
    );
}
